import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import {
  ApiHandlerService,
  DomainLookupsRequest,
  DynamicFilters,
  Lookups,
  LookupsRequest,
  ReportParamsRequest,
  ReportTemplate,
  SessionService,
  VerificationRequestBodyStrategy,
} from '@everymatrix-base-components/utils';
import { environment } from 'environments/environment.default';
import { HttpClient } from '@angular/common/http';

//TODO: Create and move interfaces to general lib
export interface AdditionalParams {
  [key: string]: any;
}

export interface ResultObject {
  p_additional_params: AdditionalParams;
  [key: string]: any;
}

export interface ReportConfig {
  offset?: number;
  template?: ReportTemplate | null | undefined;
  limit: number;
  allFilters: DynamicFilters[];
  formData: AdditionalParams;
  columns: string[];
  executionType: string;
  isGrandTotal?: boolean;
  sorting?: { field: string | null; value: 'asc' | 'desc' | null };
}
@Injectable({
  providedIn: 'root',
})
export class VerificationService {
  private initDataSubject = new BehaviorSubject<any>(null);
  initData$ = this.initDataSubject.asObservable();
  sessionData: any;

  constructor(
    private apiHandlerService: ApiHandlerService,
    private sessionService: SessionService,
    private verificationRequestBodyStrategy: VerificationRequestBodyStrategy,
    private httpClient: HttpClient
  ) {
    this.sessionData = this.sessionService.sessionData;
  }

  setInitData(data: any): void {
    this.initDataSubject.next(data);
  }

  runReport(config: ReportConfig): Observable<any> {
    const url = this.apiHandlerService.getACSBaseUrl('mm_verification_report');
    const body = this.verificationRequestBodyStrategy.buildRequestBody(config);

    return this.apiHandlerService
      .Post<any, any>(url, { p_report_params: body })
      .pipe(
        map((data) => {
          if (!data) {
            throw new Error('Server returned empty response');
          }
          if (
            ((data.errorCode || data.ok === false) && data.message) ||
            (Array.isArray(data) && data[0]?.errorCode)
          ) {
            throw new Error(data.message || 'Server responded with an error.');
          }
          return data.result;
        })
      );
  }

  getLookups(): Observable<Lookups> {
    const url = this.apiHandlerService.getACSBaseUrl(
      'rep_mm_report_params_lookup_bq',
      'ce'
    );
    const body = {
      p_report_params: {
        p_domains: [
          this.sessionService.sessionData &&
            this.sessionService.sessionData['Domains'],
        ],
        p_param_name:
          'verification_status, verification_vendor_name, verification_type, verification_source, verification_manual_clarification, documents_type, documents_status, registration_country_code, merchant',
        p_filters: {},
        p_environment:
          this.sessionService.sessionData &&
          this.sessionService.sessionData['Environment'],
      },
    };
    return this.apiHandlerService.Post<
      ReportParamsRequest<LookupsRequest>,
      Lookups
    >(url, body);
  }

  getDomainLookups(): Observable<{
    domains_mm: Lookups;
    permissions_data: {
      p_permissions: string[];
      p_permissions_context: string;
    };
  }> {
    const url = this.apiHandlerService.getACSBaseUrl('domains_lookup', 'ce');
    const body = {
      p_report_params: {
        p_domains: [this.sessionService.sessionData?.Domains],
        p_level: 'operator',
        p_param_name: ['domains_mm'],
        p_permissions: this.sessionService.sessionData?.DomainPermissions,
        p_permissions_context: 'report',
        p_environment: this.sessionService.sessionData?.Environment,
        p_user_id: this.sessionService.sessionData?.Username,
      },
    };
    return this.apiHandlerService.Post<
      ReportParamsRequest<DomainLookupsRequest>,
      {
        domains_mm: Lookups;
        permissions_data: {
          p_permissions: string[];
          p_permissions_context: string;
        };
      }
    >(url, body);
  }

  getReportFilters(): Observable<DynamicFilters[]> {
    const url = this.apiHandlerService.getACSBaseUrl('mm_columns_selector');
    const body = {
      p_report_params: {
        p_domains: [
          this.sessionService.sessionData &&
            this.sessionService.sessionData['Domains'],
        ],
        p_report_id: 'mm_verification_report',
      },
    };

    return this.apiHandlerService.Post<any, DynamicFilters[]>(url, body);
  }

  csvLoad(resource: any) {
    const match = resource.match(/reports\/.*?\.csv/);
    const result = match ? match[0].replace(/\.csv$/, '') : null;
    const url = `${environment.BASE_ACS_URL}acs-proxy?namespace=dm-gcs-docs&bucketName=dm-storage-stage&resourceName=get_google_storage_csv&fileType=csv&filePath=${result}&expiresInSeconds=600`;
    return this.httpClient.post(
      url,
      {
        p_report_params: {
          p_report_params: {
            p_domains: [this.sessionService.sessionData?.Domains],
          },
        },
      },
      { responseType: 'text' }
    );
  }
}
